/**
 * Summary:
 * This file generates a variable runtimeConfig that will have strings that get replaced via docker-entrypoint.sh during deployment
 * But when running on local vite dev server, it will just use import.meta.env
 *
 * At the bottom it finally exports a getter for the env object to be used in the project.
 */

const runtimeConfig = {
    // the ${VITE_...} part gets replaced with envsubst in docker-entrypoint.sh
    VITE_ORGANIZATION_API_HOST: '${VITE_ORGANIZATION_API_HOST}',
    VITE_DOTNET_ORGANIZATION_API_HOST: '${VITE_DOTNET_ORGANIZATION_API_HOST}',
    VITE_INFRASTRUCTURE_API_HOST: '${VITE_INFRASTRUCTURE_API_HOST}',
    VITE_INTEGRATION_API_HOST: '${VITE_INTEGRATION_API_HOST}',
    VITE_TELEMETRY_API_HOST: '${VITE_TELEMETRY_API_HOST}',
    VITE_AUTH_SSO_MODE: '${VITE_AUTH_SSO_MODE}',
} as const;

type RuntimeEnvKey = keyof typeof runtimeConfig;

// Make this writable so we can assign to it when constructing env
type RuntimeEnv = {
    -readonly [K in RuntimeEnvKey]: ImportMetaEnv[K];
};

const RUNTIME_ENV_KEYS = Object.keys(runtimeConfig) as RuntimeEnvKey[];
const DEFAULT_RUNTIME_ENV_VALUES: Partial<Record<RuntimeEnvKey, string>> = {
    VITE_AUTH_SSO_MODE: 'enforced', // `disabled`, `optional`, or `enforced`.
};

let alertHasBeenShown = false;
let cachedEnv: Readonly<RuntimeEnv> | null = null;

function getEnvVar<K extends RuntimeEnvKey>(key: K): ImportMetaEnv[K] {
    const value = runtimeConfig[key] as string;
    const defaultValue = DEFAULT_RUNTIME_ENV_VALUES[key] as ImportMetaEnv[K] | undefined;

    // If value still contains "VITE_" then envsubst did not run.
    // If value is empty string, envsubst ran but the environment variable was missing.
    if (value.includes('VITE_') || value === '') {
        // Fallback to Vite dev-server env
        const ret = import.meta.env[key];

        if (ret === undefined || ret === '') {
            if (defaultValue !== undefined) {
                return defaultValue;
            }
        }

        if (ret === undefined) {
            if (!alertHasBeenShown) {
                alert('Environment variables missing! See console for more.');
                alertHasBeenShown = true;
            }
            console.error('Environment variable missing: ', key);
        }

        return ret; // as ImportMetaEnv[K];
    }

    return value; // as ImportMetaEnv[K];
}

function buildEnv(): Readonly<RuntimeEnv> {
    const envObject = {} as RuntimeEnv;

    for (const key of RUNTIME_ENV_KEYS) {
        envObject[key] = getEnvVar(key);
    }

    return envObject;
}

export function getEnv(): Readonly<RuntimeEnv> {
    if (cachedEnv === null) {
        cachedEnv = buildEnv();
    }
    return cachedEnv;
}
